import { Link, useLocation } from "react-router-dom";
import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";

// Routes that should never show an auto-generated breadcrumb,
// either because they are root/utility surfaces or because the
// page renders its own context-aware breadcrumb.
const SKIP_PREFIXES = [
  "/admin",
  "/embed",
  "/brief",
  "/style-preview",
  "/video-test-preview",
];

// Friendly labels for known top-level / common segments.
const LABELS: Record<string, string> = {
  solutions: "Solutions",
  industries: "Industries",
  products: "Products",
  service: "Service",
  "how-to-buy": "How to Buy",
  about: "About",
  resources: "Resources",
  research: "Research",
  blog: "Blog",
  news: "News",
  partners: "Partners",
  contact: "Contact",
  pricing: "Pricing",
  plans: "Plans",
  faqs: "FAQs",
  cookies: "Cookies",
  privacy: "Privacy",
  terms: "Terms",
  subscribe: "Subscribe",
  unsubscribe: "Unsubscribe",
  "supported-phones": "Supported Phones",
  "company-phones": "Company Phones",
  "personal-phones": "Personal Phones",
  "country-availability": "Country Availability",
  "global-esim": "Global eSIM",
  "global-service": "Global Service",
  "how-it-works": "How It Works",
  "value-calculator": "Value Calculator",
  "approved-partners": "Approved Partners",
  "partner-directory": "Partner Directory",
  "where-to-buy": "Where to Buy",
  "leadership-team": "Leadership Team",
  "twenty-years": "20 Years",
  "frost-sullivan-award": "Frost & Sullivan Award",
  "fixed-mobile-convergence": "Fixed Mobile Convergence",
  "better-business": "Better Business",
  "executive-briefing": "Executive Briefing",
  finance: "Finance",
};

const ACRONYMS = new Set([
  "uc", "esim", "uk", "ai", "fmc", "sms", "b2b", "iot", "api", "kpi", "roi",
]);

function prettify(slug: string): string {
  return slug
    .split("-")
    .map((w) => (ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");
}

function labelFor(segment: string): string {
  return LABELS[segment] ?? prettify(segment);
}

// ---- Custom crumbs context ----
// Allows individual pages (PillarPage, ClusterPage, etc.) to publish a
// context-aware crumb trail that the sticky AutoBreadcrumb will render
// instead of its auto-derived path.
export type CustomCrumb = { label: string; href?: string };

type Ctx = {
  crumbs: CustomCrumb[] | null;
  setCrumbs: (c: CustomCrumb[] | null) => void;
};

const BreadcrumbContext = createContext<Ctx>({ crumbs: null, setCrumbs: () => {} });

const newsTitleCache = new Map<string, string>();

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [crumbs, setCrumbs] = useState<CustomCrumb[] | null>(null);
  const value = useMemo(() => ({ crumbs, setCrumbs }), [crumbs]);
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

/** Page-level hook: register a custom breadcrumb trail while mounted. */
export function usePageBreadcrumbs(crumbs: CustomCrumb[] | null | undefined) {
  const { setCrumbs } = useContext(BreadcrumbContext);
  const key = JSON.stringify(crumbs ?? null);
  useEffect(() => {
    setCrumbs(crumbs && crumbs.length ? crumbs : null);
    return () => setCrumbs(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

export function AutoBreadcrumb() {
  const { pathname } = useLocation();
  const { crumbs: customCrumbs } = useContext(BreadcrumbContext);
  const newsSlug = useMemo(() => {
    const m = pathname.match(/^\/news\/([^/]+)\/?$/);
    return m ? m[1] : null;
  }, [pathname]);
  const [newsTitle, setNewsTitle] = useState<string | null>(
    newsSlug ? newsTitleCache.get(newsSlug) ?? null : null,
  );
  useEffect(() => {
    if (!newsSlug) { setNewsTitle(null); return; }
    const cached = newsTitleCache.get(newsSlug);
    if (cached) { setNewsTitle(cached); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("news_articles")
        .select("title")
        .or(`internal_slug.eq.${newsSlug},id.eq.${newsSlug}`)
        .maybeSingle();
      if (cancelled) return;
      if (data?.title) {
        newsTitleCache.set(newsSlug, data.title);
        setNewsTitle(data.title);
      }
    })();
    return () => { cancelled = true; };
  }, [newsSlug]);

  if (pathname === "/" || pathname === "") return null;
  if (SKIP_PREFIXES.some((p) => pathname === p || pathname.startsWith(p))) return null;

  let crumbs: { label: string; href: string; isLast: boolean }[];
  if (customCrumbs && customCrumbs.length) {
    crumbs = customCrumbs.map((c, idx) => ({
      label: c.label,
      href: c.href ?? pathname,
      isLast: idx === customCrumbs.length - 1,
    }));
  } else {
    const rawSegments = pathname.split("/").filter(Boolean);
    // Drop non-routable router shims like /solutions/p/<slug> and /solutions/c/<slug>.
    // The "p" / "c" segments don't resolve to a real page and just confuse visitors.
    const segments = rawSegments.filter((seg, idx) => {
      if (idx === 1 && rawSegments[0] === "solutions" && (seg === "p" || seg === "c")) return false;
      return true;
    });
    if (segments.length === 0) return null;
    crumbs = segments.map((seg, idx) => {
      // Rebuild href against the original path so links still point at real routes.
      let href: string;
      if (rawSegments[0] === "solutions" && (rawSegments[1] === "p" || rawSegments[1] === "c")) {
        // idx 0 → /solutions, idx 1 (last) → full /solutions/p|c/<slug>
        href = idx === 0 ? "/solutions" : pathname;
      } else {
        href = "/" + segments.slice(0, idx + 1).join("/");
      }
      const isLast = idx === segments.length - 1;
      let label = labelFor(seg);
      if (isLast && newsSlug && seg === newsSlug) {
        label = newsTitle ?? "Article";
      }
      return { label, href, isLast };
    });
  }

  return (
    <div className="bg-background border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            {crumbs.map((c) => (
              <span key={c.href} className="contents">
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  {c.isLast || !c.href ? (
                    <BreadcrumbPage>{c.label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link to={c.href}>{c.label}</Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </span>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
    </div>
  );
}

export default AutoBreadcrumb;